<script lang="ts">
	import "@glidejs/glide/dist/css/glide.core.min.css";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import * as Glide from "@glidejs/glide";
	import { disableGlidePerSlideAmount } from "../../utils/disableGlidePerSlideAmount.js";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import { appName } from "../../../../core/schema/names/appName.js";
	import logo from "../../../../core/assets/logo.svg";
	import type { ActionReturn } from "svelte/action";
	import { tick } from "svelte";

	export let images: UploadedFile[];
	export let product: ProductForCustomer;
	export let gliderClass = "";
	export { gliderClass as class };

	const { TENANT_LOCALE } = getGlobalSettings();

	let glideInstance: Glide | undefined;
	function createGlideInstance(element: HTMLElement): void {
		glideInstance?.destroy();

		const glideOptions = {
			type: "carousel",
			startAt: 0,
			focusAt: "center",
			perView: 2.75,
			autoplay: 7000,
			hoverpause: false,
			animationDuration: 650,
			animationTimingFunc: "ease-in-out",
			gap: 9,
			keyboard: true,
			cloningRatio: 2,
			breakpoints: {
				736: { perView: 2.7 },
				720: { perView: 2.6 },
				670: { perView: 2.35 },
				590: { perView: 2.2 },
				570: { perView: 2.1 },
				540: { perView: 2 },
				515: { perView: 1.85 },
				480: { perView: 1.75 },
				455: { perView: 1.65 },
				420: { perView: 1.5 },
				370: { perView: 1.35 },
				340: { perView: 1.225 },
			},
		} satisfies Partial<Glide.Options>;

		// eslint-disable-next-line new-cap
		const glide = new Glide.default(element, glideOptions).mount();
		glide.on("resize", () => {
			disableGlidePerSlideAmount(glide, element);
		});

		glideInstance = glide;
	}

	function glide(element: HTMLDivElement): ActionReturn {
		createGlideInstance(element);

		return {
			destroy(): void {
				glideInstance?.destroy();
			},
		};
	}

	let glideElement = null as HTMLElement | null;
	async function onImagesChange(_images: UploadedFile[]): Promise<void> {
		if (glideElement) {
			glideInstance?.destroy();
			glideInstance = undefined;
			await tick();
			createGlideInstance(glideElement);
		}
	}
	$: void onImagesChange(images);
</script>

{#if images.length > 0}
	<div class={gliderClass}>
		<div bind:this={glideElement} use:glide class="glide">
			<div class="glide__track" data-glide-el="track">
				<div class="glide__slides">
					{#each images as image, index (image.path)}
						<DynamicPicture
							class="glide__slide !h-[16rem]"
							imgClass="max-w-full h-full w-auto rounded-lg aspect-square object-cover"
							{image}
							width={256}
							height={256}
							bucket="storage"
							loading="eager"
							alt="Fotografie – {product.name[TENANT_LOCALE]} ({index + 1}/{images.length})"
							preload
						/>
					{/each}
				</div>
				<div class="flex justify-center">
					<div class="glide__bullets bg-secondary-150 mt-5 flex h-0.5 w-10/12" data-glide-el="controls[nav]">
						{#each images as _image, index}
							<span
								class="glide__bullet text-secondary hover:bg-secondary-300 w-full cursor-pointer transition-all duration-200"
								data-glide-dir="={index}"
							></span>
						{/each}
					</div>
				</div>
			</div>
		</div>
	</div>
{:else}
	<div class="flex justify-center">
		<div class="bg-secondary-40 group flex h-[17rem] w-[90%] items-center justify-center md:hidden">
			<StaticPicture
				alt="Logo {appName}"
				image={logo}
				class="w-24 opacity-50 transition-all duration-300 group-hover:opacity-100"
				loading="eager"
				width={96}
				height={96}
				preload
			/>
		</div>
	</div>
{/if}
